import React, { useState } from "react";
import { Modal, Input, Button, Alert, Space, Typography } from "antd";
import { StatusTag } from "../../../components/shared";
import { CheckCircleOutlined, LinkOutlined } from "@ant-design/icons";
import { Medication, SCRIPT_STATUS_CONFIG } from "../../../types/healthcare/MedicationTypes";
import { submitMedicationEscript } from "../../../api/patient/PatientMedicationApi";
import { useNotifications } from "../../../context/NotificationContext";
import type { UserUuid } from "../../../types/identity";

const { TextArea } = Input;
const { Text } = Typography;

interface Props {
	medication: Medication | null;
	visible: boolean;
	patientId: UserUuid;
	onClose: () => void;
	onSuccess: () => void;
}

const ScriptUploadModal: React.FC<Props> = ({ medication, visible, patientId, onClose, onSuccess }) => {
	const [escriptUrl, setEscriptUrl] = useState("");
	const [notes, setNotes] = useState("");
	const [uploading, setUploading] = useState(false);
	const { showError, showSuccess } = useNotifications();

	const handleClose = () => {
		if (uploading) return;
		setEscriptUrl("");
		setNotes("");
		onClose();
	};

	const isValidUrl = (value: string) => {
		try {
			const url = new URL(value);
			return url.protocol === "https:" || url.protocol === "http:";
		} catch {
			return false;
		}
	};

	const handleSubmit = async () => {
		if (!medication) return;
		setUploading(true);
		try {
			if (!isValidUrl(escriptUrl)) {
				showError("Please enter a valid e-script URL.");
				return;
			}
			await submitMedicationEscript(patientId, medication.id, escriptUrl, notes || undefined);
			showSuccess("E-script submitted successfully. Your provider will review it shortly.");

			handleClose();
			onSuccess();
		} catch {
			showError("Failed to submit e-script. Please try again.");
		} finally {
			setUploading(false);
		}
	};

	const canSubmit = isValidUrl(escriptUrl);

	return (
		<Modal
			title="Upload Prescription Script"
			open={visible}
			onCancel={handleClose}
			width={560}
			footer={[
				<Button key="cancel" onClick={handleClose} disabled={uploading}>
					Cancel
				</Button>,
				<Button key="submit" type="primary" icon={<CheckCircleOutlined />} loading={uploading} disabled={!canSubmit} onClick={handleSubmit}>
					{uploading ? "Submitting…" : "Submit Script"}
				</Button>,
			]}
			destroyOnClose
			closable={!uploading}
		>
			<Space direction="vertical" style={{ width: "100%" }} size="middle">
				{/* Medication summary */}
				{medication && (
					<div style={{ padding: "10px 14px", borderRadius: 8, border: "1px solid #f0f0f0" }}>
						<Text strong style={{ fontSize: 14, display: "block" }}>
							{medication.medication_name}
						</Text>
						<Text type="secondary" style={{ fontSize: 12 }}>
							{medication.dosage}
							{medication.frequency ? ` · ${medication.frequency}` : ""}
						</Text>
						{medication.script_status && (
						<StatusTag
							status={
								medication.script_status === "verified" ? "completed" :
								medication.script_status === "expired" ? "expired" :
								medication.script_status === "pending" ? "pending" : "ordered"
							}
							label={SCRIPT_STATUS_CONFIG[medication.script_status].label}
							size="small"
						/>
						)}
					</div>
				)}

				<Alert message="Enter the URL or token from your electronic prescription service. Your provider will verify it before dispensing." type="info" showIcon />

				<div>
					<Text style={{ fontSize: 12, display: "block", marginBottom: 6 }}>
						E-Script URL <Text type="danger">*</Text>
					</Text>
					<Input
						prefix={<LinkOutlined style={{ color: "#1677ff" }} />}
						value={escriptUrl}
						onChange={(e) => setEscriptUrl(e.target.value)}
						placeholder="https://escripts.example.com/rx/abc123"
						disabled={uploading}
						status={escriptUrl && !isValidUrl(escriptUrl) ? "error" : undefined}
					/>
					{escriptUrl && !isValidUrl(escriptUrl) && (
						<Text type="danger" style={{ fontSize: 11, marginTop: 4, display: "block" }}>
							Please enter a valid URL (must start with http:// or https://)
						</Text>
					)}
				</div>

				{/* Notes */}
				<div>
					<Text style={{ fontSize: 12, display: "block", marginBottom: 6 }}>Notes (optional)</Text>
					<TextArea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. repeat script, original issued by Dr Smith on 12 Jan 2025" rows={3} maxLength={1000} disabled={uploading} showCount />
				</div>
			</Space>
		</Modal>
	);
};

export default ScriptUploadModal;

